import { valueIsNotNullOrUndefined, addMoneys } from '@afterpay/utils'
import { useCheckoutState, useDonation, useIsCheckoutLoadingOrUpdating, useIsExpress } from '../../state'
import { Money } from '@afterpay/types'

type OrderBreakdownType = 'Full' | 'DiscountAndDonation' | 'None'

const defaultOrderBreakdownType = (hasPromoOrDonation: boolean): OrderBreakdownType => {
  if (hasPromoOrDonation) {
    return 'DiscountAndDonation'
  }
  return 'None'
}

export const useOrderBreakdownType = (merchantProvidedShippingAmount?: Money, merchantProvidedTaxesAmount?: Money): OrderBreakdownType => {
  const { started, checkout } = useCheckoutState()
  const isLoadingOrUpdating = useIsCheckoutLoadingOrUpdating()
  const isExpressCheckout = useIsExpress()
  const { donationAmount } = useDonation()
  const hasPromoOrDonation = valueIsNotNullOrUndefined(started?.promotion) || donationAmount > 0

  if (isLoadingOrUpdating) {
    return 'None'
  }

  // Hide order breakdown when standard checkout and the final amount is unchanged
  if (!isExpressCheckout && !hasPromoOrDonation) {
    return 'None'
  }

  const orderTotal = started?.consumerAmount.amount
  const merchantProvidedTotal = addMoneys({
    moneys: [checkout?.amount, merchantProvidedShippingAmount, merchantProvidedTaxesAmount].filter(valueIsNotNullOrUndefined),
  })

  // Final condition is to show order breakdown only if our calculated order total is equal to the merchant provided total
  // This can be true/false for express due to relying on merchant values, but for standard checkout it should always be true
  const isMerchantTotalMatching = orderTotal === merchantProvidedTotal
  return isMerchantTotalMatching ? 'Full' : defaultOrderBreakdownType(hasPromoOrDonation)
}

export const useIsOrderBreakdownVisible = (orderBreakdownType: OrderBreakdownType): boolean => {
  return orderBreakdownType === 'Full'
}
